import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { employeeKeys } from "./employee";
import { useDispatch } from "react-redux";
import { transformDate } from "../utils/date";
import { TCOKeys } from "./tco";

export const vehiculesKeys = {
  all: (
    fleetId: number,
    page: number,
    per_page: number,
    filter: string,
    status: string
  ) =>
    [
      "vehicles",
      "fleetId",
      fleetId,
      "page",
      page,
      "per_page",
      per_page,
      "filter",
      filter,
      "status",
      status,
    ] as const,
  vehiculesByEmployee: (fleetId: number, employeeId: number) =>
    ["vehicules", "idFleet", fleetId, "employee", employeeId] as const,
  documents: (vehicleId: number) =>
    ["vehicule", "documents", "vehicleId", vehicleId] as const,
  events: (vehicleId: number) =>
    ["vehicule", "events", "vehicleId", vehicleId] as const,
  eventsById: (vehicleId: number, eventsId: number) =>
    ["vehicule", vehicleId, "events", eventsId] as const,
  eventsDocuments: (vehicleId: number, eventsId: number) =>
    ["vehicule", vehicleId, "events", eventsId, "documents"] as const,
  eventsDocumentsById: (
    vehicleId: number,
    eventsId: number,
    documentId: number
  ) =>
    [
      "vehicule",
      vehicleId,
      "events",
      eventsId,
      "documents",
      documentId,
    ] as const,
  vehicule: (vehicleId: number) => ["vehicule", "id", vehicleId] as const,
  mileage: (vehicleId: number) =>
    ["vehicule", "mileage", vehicleId] as const,
  vehiculeNumber: (fleetId: number) =>
    ["vehiculeNumber", "id", fleetId] as const,
  batteryStats: (vehicleId: number, fromDate?: string, toDate?: string) => 
    ["vehicule", "batteryStats", vehicleId, fromDate, toDate] as const,
};

interface ResponseGetVehicules {
  data: {
    data: TVehicule[];
    total: number;
  };
}

// Battery stats types based on your API response
interface BatteryStats {
  available: boolean;
  vin: string;
  stateOfHealth: number;
  final_score: number;
  final_grade: string;
  score_details: {
    distance: {
      total_km: number;
    };
    charging_behavior: {
      deep_discharges: {
        count: number;
        score: number;
        mark: string;
      };
      fast_charges: {
        count: number;
        full_fast_charges: number;
        score: number;
        mark: string;
      };
      full_charges: {
        count: number;
        score: number;
        mark: string;
      };
    };
    battery_management: {
      time_above_80_percent: number;
      time_below_20_percent: number;
      optimal_range_usage: number;
    };
    temperature_impact: {
      score: number;
      mark: string;
    };
    overall_marks: {
      temperature_impact: string;
      fast_charging: string;
      deep_discharge: string;
      full_charge: string;
      final_score: string;
    };
  };
  analysis: {
    overall_grade: string;
    recommendations: any[];
    strengths: any[];
    areas_for_improvement: any[];
  };
  period: {
    from_date: string;
    to_date: string;
  };
  recommendations: any[];
  summary: {
    health_status: string;
    primary_concern: string;
    strongest_area: string;
    improvement_potential: string;
  };
}

interface BatteryStatsResponse {
  success: boolean;
  data: {
    vehicle: {
      id: number;
      number: string;
      vin: string;
      type: string;
      gravage: string | null;
      picture: string;
      register_step: string | null;
      boite: string | null;
      puissance: string | null;
      carburant: string;
      mise_en_circulation: string;
      statut: string;
      commissioning_date: string | null;
      full_name: string;
      created_at: string;
      updated_at: string;
    };
    battery_stats: BatteryStats;
    retrieved_at: string;
  };
  message: string;
}

/**
 * Server-side filters for the vehicle list.
 *
 * Every field is applied by the API, not in the browser: the fleet screen is
 * paginated, so filtering the 25 rows currently on screen would silently
 * search a 25-row slice of a 2000-vehicle fleet.
 */
export type TVehicleListFilters = {
  /** Free-text across plate, VIN, brand, model and collaborator name. */
  filter?: string;
  /** One status, or several comma-separated (O | A | F | W). */
  status?: string;
  brand_id?: number | null;
  carburant?: string;
  department_id?: number | null;
  /** 'yes' = has an active collaborator, 'no' = unassigned. */
  assigned?: "yes" | "no" | "";
  /** Renewal planning: contracts ending on or before this date. */
  contract_end_before?: string;
  contract_end_after?: string;
  lbc_eligible?: "yes" | "no" | "";
  sort?:
    | "plate_asc"
    | "plate_desc"
    | "brand_asc"
    | "contract_end_asc"
    | "contract_end_desc"
    | "recent";
};

/** Drops empty values so they never reach the query string or the cache key. */
const cleanFilters = (filters: TVehicleListFilters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );

export const useGetAllVehiculesFleet = (
  fleetId: number,
  page: number = 1,
  per_page: number = 10,
  filter: string = "",
  status: string = "",
  filters: TVehicleListFilters = {}
) => {
  // `filter` and `status` stay as positional arguments so the dozen existing
  // call sites keep working unchanged; anything richer goes in `filters`.
  const params = cleanFilters({
    ...filters,
    filter: filter.trim().length > 0 ? filter : filters.filter,
    status: status.trim().length > 0 ? status : filters.status,
  });

  const { data, error, ...res } = useQuery({
    // The whole param set is part of the key. Keying on page/search alone
    // would serve a cached page from a different filter combination.
    queryKey: [...vehiculesKeys.all(fleetId, page, per_page, filter, status), params],
    queryFn: (): Promise<ResponseGetVehicules> =>
      axios.get(`/fleets/${fleetId}/vehicles`, {
        params: { page, per_page, ...params },
      }),
    // FIX: prevent firing with invalid fleetId (0, NaN) which would hit
    // /fleets/0/vehicles and return an error from the quota middleware.
    enabled: typeof fleetId === 'number' && !isNaN(fleetId) && fleetId > 0,
    staleTime: 0,
    // Keeps the previous page on screen while the next one loads instead of
    // flashing an empty table on every filter change.
    placeholderData: (prev) => prev,
  });
  return {
    vehicules: data?.data?.data ?? [],
    totalCount: data?.data?.total,
    ...res,
  };
};

/** One option in a filter dropdown, with the number of rows it would match. */
export type TVehicleFilterOption = {
  value: string | number;
  label?: string;
  count: number;
};

export type TVehicleFilterOptions = {
  statuses: TVehicleFilterOption[];
  brands: TVehicleFilterOption[];
  fuels: TVehicleFilterOption[];
  departments: TVehicleFilterOption[];
  contract_expiring: { days: number; until: string; count: number }[];
};

/**
 * Filter options built from the fleet's own data.
 *
 * Fetched rather than hardcoded so the dropdowns can never offer a brand the
 * fleet does not own, nor miss one that was added this morning. The counts let
 * the UI show a filter's yield before it is applied.
 */
export const useGetVehicleFilterOptions = (fleetId: number) => {
  const { data, ...res } = useQuery({
    queryKey: ["vehicles", "filter-options", fleetId],
    queryFn: async (): Promise<TVehicleFilterOptions> => {
      const res = await axios.get(`/fleets/${fleetId}/vehicle-filter-options`);
      return res?.data ?? res;
    },
    enabled: typeof fleetId === "number" && !isNaN(fleetId) && fleetId > 0,
    // The shape of a fleet changes when vehicles are added, not between
    // keystrokes.
    staleTime: 5 * 60 * 1000,
  });

  return {
    options: data ?? {
      statuses: [],
      brands: [],
      fuels: [],
      departments: [],
      contract_expiring: [],
    },
    ...res,
  };
};

export const useGetOneVehicle = (fleetId: number, vehicleId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.vehicule(vehicleId),
    queryFn: async (): Promise<TVehiculeOne> => {
      const data = await axios.get(`/fleets/${fleetId}/vehicles/${vehicleId}`);
      return data?.data;
    },
    enabled: !!fleetId && !!vehicleId && fleetId > 0 && vehicleId > 0,
  });
  return { vehicule: data, ...res };
};

/**
 * Relevé kilométrique courant du véhicule.
 *
 * POURQUOI CE HOOK EXISTE
 * -----------------------
 * L'endpoint GET /vehicle/{id}/mileage existait déjà côté API, mais AUCUN
 * appelant web ne le consommait. Le panneau Kilométrage de la fiche véhicule
 * affichait donc `contrat.contract_km` — le forfait CONTRACTUEL — sur ses deux
 * lignes, et un relevé saisi par le conducteur depuis l'app mobile n'apparaissait
 * nulle part sur Fleet.
 *
 * Le véritable relevé vient des événements du véhicule (le schéma n'a pas de
 * colonne mileage sur `vehicle`), donc c'est bien la même source de vérité que
 * la règle de cohérence serveur.
 *
 * La fiche véhicule (`useGetOneVehicle`) porte désormais aussi `currentMileage`
 * et `lastMileageDate` ; ce hook reste utile là où l'on n'a pas chargé la fiche
 * complète.
 */
export const useGetVehicleMileage = (vehicleId: number) => {
  const { data, ...res } = useQuery({
    queryKey: vehiculesKeys.mileage(vehicleId),
    queryFn: async (): Promise<number | null> => {
      const response = await axios.get(`/vehicle/${vehicleId}/mileage`);
      const value = response?.data?.mileage;
      return typeof value === "number" ? value : null;
    },
    enabled: !!vehicleId && vehicleId > 0,
  });

  return { mileage: data ?? null, ...res };
};

/**
 * Relance le collaborateur affecté au véhicule pour qu'il saisisse son relevé
 * kilométrique.
 *
 * NE PAS confondre avec useReminderDocumentMissing (api/document.ts), qui
 * relance les PIÈCES du collaborateur (permis, carte d'identité) et n'a sa
 * place que sur la fiche collaborateur. Le bouton du panneau Kilométrage
 * pointait dessus : il envoyait un mail sur la carte d'identité et ne
 * produisait aucune notification côté mobile.
 *
 * Le backend renvoie un message déjà traduit ; on l'affiche tel quel plutôt
 * que de le redupliquer ici, avec une clé locale en repli.
 */
export const useSendMileageReminder = (fleetId: number, vehicleId: number) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `/fleets/${fleetId}/vehicles/${vehicleId}/mileage-reminder`
      );
      return res?.data;
    },
    onSuccess: (data: any) => {
      toast.success(
        data?.message ?? t("panel.mileage.reminderSent", "Reminder sent")
      );
    },
    onError: (err: any) => {
      toast.error(
        String(
          err?.response?.data?.message ??
            t("panel.mileage.reminderFailed", "Failed to send the reminder")
        )
      );
    },
  });
};

export const useGetVehiculesByEmployee = (
  fleetId: number,
  employeeId: number
) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.vehiculesByEmployee(fleetId, employeeId),
    queryFn: async (): Promise<TVehicule[]> => {
      const data = await axios.get(
        `/fleets/${fleetId}/employees/${employeeId}/vehicles`
      );
      return data?.data;
    },
    staleTime: 0, // Always fetch fresh data
    enabled: !!fleetId && !!employeeId,
  });

  return { vehicules: data ?? [], ...res };
};

interface RequestUpdateStatusVehicle {
  status: number;
}

export const useUpdateStatusVehicle = (fleetId: number, vehiculeId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updateStatus: RequestUpdateStatusVehicle) => {
      const data = await axios.put(
        `/fleets/${fleetId}/vehicles/${vehiculeId}/status`,
        updateStatus
      );
      return data.data?.vehicule;
    },
    onSuccess: (_) => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "fleetId", fleetId] } as any);
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export interface RequestCreateVehicule {
  // required
  vin: string;
  number: string;
  owner_company_id?: number;
  brand_model_id: number;
  vehicle_status: string;
  monthly_average_cost: number;
  // not required
  identification_id?: number;
  picture?: string;
  type?: string;
  gravage?: string;
  register_step?: number;
  boite?: string;
  puissance?: string;
  carburant?: string;
  mise_en_circulation?: string;
  commissioning_date?: string;
}

export const useCreateVehicule = (fleetId: number, onSave?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (createVehicule: RequestCreateVehicule) => {
      const data = await axios.post(
        `companies/${fleetId}/vehicles/add`,
        createVehicule
      );
      return data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "fleetId", fleetId] } as any);

      const statusKeys = TCOKeys.vehicles(fleetId);
      queryClient.refetchQueries({ queryKey: statusKeys });

      // Energy mix / KPI cards read from the dashboard analytics query;
      // invalidate it (any range) so they reflect the new vehicle immediately.
      queryClient.invalidateQueries({
        queryKey: ["dashboard-analytics", "idFleet", fleetId],
      });

      toast.success(t("common.save", "Change saved"));

      dispatch({ type: "NEWLY_CREATED_VEHICULE", data: data });
      onSave && onSave();
    },
    onError: (err: any) => {
      // Prefer the stable error_key; fall back to the legacy message-keyed
      // lookup so locale files that still carry the old English-sentence keys
      // keep working. See assignVehicleErrorMessage() for the full rationale.
      toast.error(
        t(
          `errorAddVehicle.${
            err?.response?.data?.error_key ?? err?.response?.data?.message
          }`,
          String(err?.response?.data?.message)
        )
      );
    },
  });
};

interface RequestUpdateVehiculeFleet {
  vehicle_status: string;
  monthly_average_cost: number;
}

export const useUpdateVehiculeFleet = (fleetId: number, vehiculeId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updateVehicule: RequestUpdateVehiculeFleet) => {
      const data = await axios.put(
        `companies/${fleetId}/vehicles/${vehiculeId}/update`,
        updateVehicule
      );
      return data.data;
    },
    onSuccess: (_) => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "fleetId", fleetId] } as any);
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      // Prefer the stable error_key; fall back to the legacy message-keyed
      // lookup so locale files that still carry the old English-sentence keys
      // keep working. See assignVehicleErrorMessage() for the full rationale.
      toast.error(
        t(
          `errorAddVehicle.${
            err?.response?.data?.error_key ?? err?.response?.data?.message
          }`,
          String(err?.response?.data?.message)
        )
      );
    },
  });
};

interface RequestUpdateVehicule {
  // required
  vin: string;
  number: string;
  brand_model_id: number;
  // not required
  identification_id?: number;
  picture?: string;
  type?: string;
  gravage?: string;
  register_step?: number;
  boite?: string;
  puissance?: string;
  carburant?: string;
  mise_en_circulation?: string;
  commissioning_date?: string;
}

export const useUpdateVehicule = (
  fleetId: number,
  vehiculeId: number,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updateVehicule: RequestUpdateVehicule) => {
      const data = await axios.put(
        `/vehicle/update/${vehiculeId}`,
        updateVehicule
      );
      return data.data;
    },
    onSuccess: (_) => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "fleetId", fleetId] } as any);
      toast.success(t("common.changeSaved", "Change saved"));
      onSuccess && onSuccess();
    },
    onError: (err: any) => {
      // Prefer the stable error_key; fall back to the legacy message-keyed
      // lookup so locale files that still carry the old English-sentence keys
      // keep working. See assignVehicleErrorMessage() for the full rationale.
      toast.error(
        t(
          `errorAddVehicle.${
            err?.response?.data?.error_key ?? err?.response?.data?.message
          }`,
          String(err?.response?.data?.message)
        )
      );
    },
  });
};

export const useDeleteVehicule = (
  fleetId: number,
  vehiculeId: number,
  onSuccess?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.delete(`/vehicle/delete/${vehiculeId}`);
      return data.data;
    },
    onSuccess: (_) => {
      queryClient.invalidateQueries({ queryKey: ["vehicles", "fleetId", fleetId] } as any);
      toast.success(t("vehicule.vehicleDisabled", "Vehicle disabled"));
      onSuccess && onSuccess();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useGetAllVehiculesDocuments = (vehicleId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.documents(vehicleId),
    queryFn: async (): Promise<TDocument[]> => {
      const data = await axios.get(`/vehicle/${vehicleId}/documents`);
      return data?.data;
    },
    enabled: !!vehicleId && vehicleId > 0,
  });

  return { documents: data ?? [], ...res };
};

export const useGetAllVehiculesEvents = (vehicleId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.events(vehicleId),
    queryFn: async (): Promise<TEvent[]> => {
      const data = await axios.get(`/vehicle/${vehicleId}/events`);
      return data?.data;
    },
    enabled: !!vehicleId && vehicleId > 0,
  });

  return { events: data ?? [], ...res };
};

export const useGetVehiculesEventsById = (
  vehicleId: number,
  eventsId: number
) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.eventsById(vehicleId, eventsId),
    queryFn: async (): Promise<TEvent> => {
      const response = await axios.get(`/vehicle/${vehicleId}/events/${eventsId}`);
      return response?.data;
    },
    enabled: !!vehicleId && !!eventsId,
  });
  
  // Return undefined instead of empty object to properly handle the null state
  return { 
    event: data as TEvent | undefined, 
    ...res 
  };
};

interface RequestCreateVehiculeEvents {
  name: string;
  datetime: string;
  user_id: number;
  event_type_id: number;
  address_id: number;
  note: string;
  url_report: string;
}

export const useCreateVehiculeEvents = (vehicleId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (createVehiculeEvent: RequestCreateVehiculeEvents) => {
      const data = await axios.post(
        `/vehicle/${vehicleId}/events`,
        createVehiculeEvent
      );
      return data.data?.events;
    },
    onSuccess: (data) => {
      const keys = vehiculesKeys.events(vehicleId);
      const prev = queryClient.getQueryData<TEvent[]>(keys);
      if (prev && data) {
        queryClient.setQueryData<TEvent[]>(keys, [...prev, data]);
      }
      toast.success(t("common.save", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useGetDocumentsForAnEventOfAVehicle = (
  vehicleId: number,
  eventId: number
) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.eventsDocuments(vehicleId, eventId),
    queryFn: async (): Promise<TDocument[]> => {
      const response = await axios.get(
        `/vehicle/${vehicleId}/events/${eventId}/documents`
      );
      return response?.data?.documents ?? [];
    },
    enabled: !!vehicleId && !!eventId,
  });
  return { documents: data ?? [], ...res };
};

export const useGetDocumentsForAnEventOfAVehicleById = (
  vehicleId: number,
  eventId: number,
  documentId: number
) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.eventsDocumentsById(vehicleId, eventId, documentId),
    queryFn: async (): Promise<TDocument> => {
      const data = await axios.get(
        `/vehicle/${vehicleId}/events/${eventId}/documents/${documentId}`
      );
      return data?.data;
    },
    enabled: !!vehicleId && !!eventId && !!documentId,
  });

  return { document: data ?? {}, ...res };
};

interface RequestUpdateAnEventForVehicule {
  name: string;
  datetime: string;
  user_id: number;
  event_type_id: number;
  address_id: number;
  note: string;
  url_report: string;
}

export const useUpdateAnEventForVehicule = (
  vehiculeId: number,
  eventId: number
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updateVehicule: RequestUpdateAnEventForVehicule) => {
      const data = await axios.put(
        `/vehicle/${vehiculeId}/events/${eventId}`,
        updateVehicule
      );
      return data.data?.event;
    },
    onSuccess: (data) => {
      const keys = vehiculesKeys.events(vehiculeId);
      const prev = queryClient.getQueryData<TEvent[]>(keys);
      if (prev && data) {
        const res = prev.map((p) => {
          if (p.id === data.id) {
            return data;
          }
          return p;
        });
        queryClient.setQueryData<TEvent[]>(keys, res);
      }
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

export const useDeleteEvents = (vehiculeId: number, eventsId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.delete(
        `/vehicle/${vehiculeId}/events/${eventsId}`
      );
      return data.data;
    },
    onSuccess: (_) => {
      const keys = vehiculesKeys.events(vehiculeId);
      const prev = queryClient.getQueryData<TEvent[]>(keys);
      if (prev) {
        const res = prev.filter((event) => event.id !== eventsId);
        queryClient.setQueryData<TEvent[]>(keys, res);
      }
      toast.success(t("common.changeSaved", "Change saved"));
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message);
    },
  });
};

interface RequestAssignVehicleToEmployee {
  start_date: string;
  end_date: string;
}

/**
 * The API used to return English sentences, and the locale files keyed their
 * translations on those exact sentences under `vehicule.<message>` (see the
 * per-locale translation.json files under src/i18n/locales).
 *
 * That contract is GONE. The API now localises `message` itself, so a French
 * user gets a French sentence and `vehicule.<message>` never matches again.
 * The failure was silent and expensive: the lookup fell through to the raw
 * message, so a plausible sentence still appeared and only the extra guidance
 * below quietly stopped being shown.
 *
 * So we key on `error_key` instead - a stable identifier the API sends
 * alongside the prose precisely so clients stop matching on sentences. The
 * translated `message` is the fallback, which is already a decent answer, and
 * the legacy `vehicule.<message>` lookup is kept last so any locale file still
 * carrying the old sentence-keyed entries keeps working until they are moved
 * over.
 */
const ASSIGN_ERROR_GUIDANCE: Record<string, string> = {
  "errors.vehicle.no_contract":
    "This vehicle has no rental contract yet. Add one from the vehicle sheet before assigning it - the signed contract PDF in the Documents card is not a rental contract.",
  "errors.vehicle.contract_other_company":
    "This vehicle's rent contract belongs to another company. Create a contract for your fleet before assigning it.",
  "errors.fleet.vehicle_already_assigned":
    "This vehicle is already assigned to someone. End the current assignment first.",
  "errors.fleet.employee_has_assignment":
    "This collaborator already has a vehicle. End that assignment first.",
  "errors.vehicle.rent_start_after_affectation":
    "The assignment cannot start before the vehicle's rent contract does.",
};

const assignVehicleErrorMessage = (
  t: (key: string, fallback: string) => string,
  message?: string,
  errorKey?: string
): string => {
  if (errorKey && ASSIGN_ERROR_GUIDANCE[errorKey]) {
    // `vehicule.<errorKey>` so a locale file can still override the guidance;
    // the curated English sentence is the default.
    return t(`vehicule.${errorKey}`, ASSIGN_ERROR_GUIDANCE[errorKey]);
  }

  if (!message) return t("vehicule.assignFailed", "Could not assign the vehicle.");

  // Legacy sentence-keyed lookup, then the API's own localised message.
  return t(`vehicule.${message}`, String(message));
};

/**
 * Bust every cache an assignment change can invalidate.
 *
 * The three assignment mutations each invalidated a DIFFERENT subset:
 *   - assign            -> vehiculesByEmployee only
 *   - useEndVehicleAssignment  -> vehiculesByEmployee only
 *   - useUnAssignVehicle...    -> employeeKeys + vehiculesByEmployee
 *
 * None of them touched the vehicle LIST (whose vehicle_status flips between
 * FREE and AFFECTED) or the single vehicle sheet (which renders the
 * COLLABORATEUR card). That matters far more here than in a typical app,
 * because queryClient.ts sets staleTime to 24h with refetchOnMount,
 * refetchOnWindowFocus and refetchOnReconnect all false - so anything not
 * explicitly invalidated stays stale for a DAY, across navigation and tab
 * switches.
 *
 * Net effect before this fix: assign a vehicle, then open its sheet, and it
 * still reads "NO COLLABORATOR ASSIGNED" while the list still shows "Libre" -
 * indefinitely. The backend was correct; the cache was not.
 *
 * Prefix matching: React Query treats a shorter key as a prefix, so
 * ['vehicles','fleetId',id] clears every page/filter/status permutation of the
 * list, and ['vehicule','id',id] clears that vehicle's sheet.
 */
const invalidateAssignmentCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  fleetId: number,
  employeeId: number,
  vehicleId: number
) => {
  // Assignment rows for this collaborator (drives the "Véhicule affecté" card).
  queryClient.invalidateQueries({
    queryKey: vehiculesKeys.vehiculesByEmployee(fleetId, employeeId),
  });
  // Collaborator list/detail - the vehicle column changes too.
  queryClient.invalidateQueries({ queryKey: employeeKeys.all(fleetId) });
  // Vehicle list, all pages/filters/statuses (vehicle_status flips).
  queryClient.invalidateQueries({
    queryKey: ["vehicles", "fleetId", fleetId],
  });
  // The vehicle sheet, which renders the COLLABORATEUR card.
  queryClient.invalidateQueries({
    queryKey: vehiculesKeys.vehicule(vehicleId),
  });
};

export const useAssignVehicleToEmployee = (
  fleetId: number,
  employeeId: number,
  vehicleId: number,
  onSend?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assignVehicle: RequestAssignVehicleToEmployee) => {
      assignVehicle = {
        start_date: transformDate(assignVehicle.start_date),
        end_date: transformDate(assignVehicle.end_date),
      };
      const data = await axios.post(
        `/fleets/${fleetId}/employees/${employeeId}/vehicles/${vehicleId}/assign`,
        assignVehicle
      );
      return data.data;
    },
    onSuccess: (_) => {
      toast.success(
        t("collaborator.vehicleWellAffected", "Vehicle well affected")
      );
      onSend && onSend();

      invalidateAssignmentCaches(queryClient, fleetId, employeeId, vehicleId);
    },
    onError: (err: any) => {
      toast.error(
        assignVehicleErrorMessage(
          t,
          err?.response?.data?.message,
          err?.response?.data?.error_key
        )
      );
    },
  });
};

export const useEndVehicleAssignment = (
  fleetId: number,
  employeeId: number,
  vehicleId: number,
  onClose?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.put(
        `/fleets/${fleetId}/employees/${employeeId}/vehicles/${vehicleId}/unassigned`
      );
      return data.data;
    },
    onSuccess: (_) => {
      toast.success(
        t("collaborator.vehicleWellRemoved", "Vehicle well removed")
      );
      onClose && onClose();

      invalidateAssignmentCaches(queryClient, fleetId, employeeId, vehicleId);
    },
    onError: (err: any) => {
      toast.error(
        assignVehicleErrorMessage(
          t,
          err?.response?.data?.message,
          err?.response?.data?.error_key
        )
      );
    },
  });
};

export const useUnAssignVehicleToEmployee = (
  fleetId: number,
  employeeId: number,
  vehicleId: number,
  onClose?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await axios.delete(
        `/fleets/${fleetId}/employees/${employeeId}/vehicles/${vehicleId}/unassigned`
      );
      return data.data;
    },
    onSuccess: (_) => {
      toast.success(
        t("collaborator.vehicleWellRemoved", "Vehicle well removed")
      );
      onClose && onClose();

      invalidateAssignmentCaches(queryClient, fleetId, employeeId, vehicleId);
    },
    onError: (err: any) => {
      toast.error(
        assignVehicleErrorMessage(
          t,
          err?.response?.data?.message,
          err?.response?.data?.error_key
        )
      );
    },
  });
};

export const useVehicleFleetNumber = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.vehiculeNumber(fleetId),
    queryFn: async (): Promise<TVehiculeOne> => {
      const data = await axios.get(`/fleets/${fleetId}/vehicle-by-number`);
      return data?.data;
    },
    enabled: !!fleetId && fleetId > 0,
    staleTime: 10000,
  });
  return { vehiculeNumber: data, ...res };
};

export const useGetVehicleBatteryStats = (vehicleId: number, fromDate?: string, toDate?: string) => {
  const { data, error, ...res } = useQuery({
    queryKey: vehiculesKeys.batteryStats(vehicleId, fromDate, toDate),
    queryFn: async (): Promise<BatteryStatsResponse> => {
      const params = new URLSearchParams();
      
      if (fromDate) {
        // Format date to match PHP validation: Y-m-d\TH:i:s (without milliseconds and timezone)
        const formattedFromDate = new Date(fromDate).toISOString().slice(0, 19);
        params.append('from_date', formattedFromDate);
      }
      
      if (toDate) {
        // Format date to match PHP validation: Y-m-d\TH:i:s (without milliseconds and timezone)
        const formattedToDate = new Date(toDate).toISOString().slice(0, 19);
        params.append('to_date', formattedToDate);
      }
      
      // Send boolean as string "1" or "0" for PHP
      params.append('include_analysis', '1');
      
      const response = await axios.get(`/fleets/vehicle/${vehicleId}/battery-stats?${params.toString()}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - battery stats don't change frequently
    enabled: !!vehicleId && vehicleId > 0,
  });
  
  return {
    batteryData: data,
    vehicleInfo: data?.data?.vehicle,
    batteryStats: data?.data?.battery_stats,
    retrievedAt: data?.data?.retrieved_at,
    ...res,
  };
};
